<script setup lang="ts">
  import type {
    NtpNewsEvent,
    NtpNewsTileType,
  } from '../../composables/analytics/types'
  import { useRedirectActions } from '../../composables/useRedirectActions'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import type { Creative } from '../../server/models/direct-ads'
  import {
    handleDirectAdClick,
    handleDirectAdRender,
  } from '../../utils/direct-ads/directAdsUtils'
  import { useFormattedTimeDifference } from '../../composables/date/useFormattedTimeDifference'
  import type { NewsCategory } from '../../server/models/news/news-article'
  import Button from '../Buttons/Button.vue'
  import Icon from '../Icon/Icon.vue'
  import { getArticleStyleVariants } from './articleStyleVariants'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'

  export interface ArticleCardProps {
    design?: 'google' | 'msn'
    variant?:
      | 'default'
      | 'yolo-l'
      | 'yolo-xl'
      | 'yolo-xxl'
      | 'yolo-full'
      | 'transparent'
      | 'title-msn'
      | 'video-s'
      | 'video-l'
      | '2x2'
      | '2x2-top-headline'
    size?: 'tiny' | 'default' | 'large'
    type?: 'video' | undefined
    tileType?: NtpNewsTileType
    tilePosition?: number

    thumbnailUrl?: string
    headline?: string
    subtitle?: string
    publisherName?: string
    publisherIconUrl?: string
    publishedDate?: string | Date
    providerName?: string

    id?: string
    articleUrl?: string // original Article URL
    articleCategory?: NewsCategory | string
    ad?: boolean
    creative?: Creative
  }

  const { t } = useI18n()
  const emit = defineEmits<{
    (e: 'click', props: ArticleCardProps): void
  }>()

  const props = withDefaults(defineProps<ArticleCardProps>(), {
    design: 'google',
    variant: 'default',
    size: 'default',
    tileType: 'title',
    tilePosition: undefined,

    ad: false,
    thumbnailUrl: '',
    headline: '',
    subtitle: '',
    publisherName: '',
    providerName: '',
    publisherIconUrl: '',
    publishedDate: '',
    articleCategory: '',
    articleUrl: '',
    id: '',
    creative: undefined,
    type: undefined,
  })

  const { getNewsArticleUrl } = useRedirectActions()

  const isDirectAd = computed(() => props.ad && props.creative !== undefined)
  const isNativeAd = computed(() => props.ad && !isDirectAd.value)

  function getEventDetailsForAnalytics(): NtpNewsEvent {
    const newsCategory = props.articleCategory.includes('NC')
      ? 'News'
      : props.articleCategory

    return {
      ntp_news_category: newsCategory,
      ntp_news_provider: props.providerName,
      ntp_news_publisher: props.publisherName,
      ntp_news_tile_style: props.tileType,
      ntp_tile_position: props.tilePosition || undefined,
      ntp_content_type: props.type || 'article',
    }
  }

  const { track } = useAnalytics()
  const userEngagement = userEngagementStore()

  function handleClick(event: MouseEvent) {
    if (props.ad && props.creative === undefined) {
      return
    }
    userEngagement.setUserEngagement('isEngaged', true)
    event.preventDefault()
    if (props.ad && props.creative !== undefined) {
      handleDirectAdClick(props.creative)
      return
    }

    emit('click', props)

    if (props.id) {
      track('NtpNewsTile_Clicked', getEventDetailsForAnalytics())
    }
  }

  function getHrefUrl() {
    if (isDirectAd.value) {
      return props.creative?.clickUrl
    }
    if (isNativeAd.value) return
    return getNewsArticleUrl(props)
  }

  onMounted(() => {
    if (props.ad && props.creative !== undefined) {
      handleDirectAdRender(props.creative)
    }
    if (!props.ad && props.thumbnailUrl) {
      track('NtpNewsTile_Rendered', getEventDetailsForAnalytics())
    }
  })

  onUpdated(() => {
    if (!props.ad && props.thumbnailUrl) {
      track('NtpNewsTile_Rendered', getEventDetailsForAnalytics())
    }
  })

  const style = computed(() => getArticleStyleVariants(props))
</script>
<!--
  To prevent ads from rendering more than once on the same tile, we use the key property.
  When the key changes, it triggers a component re-render, effectively refreshing the content
  and ensuring that content directly inserted by AY and its associated APIs is removed.
  We use the "class" attribute that comes from the feature flags as the key, since it
  distinguishes each ad placement.
-->
<template>
  <a
    :key="props.ad ? String($attrs.class) : 'news-tile'"
    :href="getHrefUrl()"
    target="_blank"
    class="relative transition duration-300"
    :class="style.wrapper"
    role="article"
    :aria-label="props.ad ? 'Partner Content' : headline"
    @click="handleClick"
  >
    <NuxtImg
      v-if="!isNativeAd && variant !== '2x2-top-headline' && style.image"
      class="transition duration-300"
      role="img"
      :aria-label="`Image for ${props.ad ? 'Partner Content' : headline}`"
      :class="style.image"
      :src="isDirectAd ? creative?.imageUrl : thumbnailUrl"
    />
    <div
      v-if="type === 'video'"
      :class="[
        'flex justify-center items-center absolute inset-0 z-10 top-20',
        style.playButton || 'h-5',
      ]"
    >
      <Icon
        icon="play-button"
        :classes="
          variant === 'video-s' || variant === 'video-l'
            ? ['h-[40px]', 'w-[40px]']
            : []
        "
      />
    </div>
    <div
      v-if="
        props.design === 'msn' &&
        props.variant === 'transparent' &&
        (props.size === 'default' || props.size === 'large')
      "
      class="absolute w-full bottom-0 h-[180px] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.5)_46.02%,rgba(0,0,0,0.5)_100%)] rounded-lg"
    />
    <template
      v-if="
        !isNativeAd && ['default', 'video-s', 'title-msn'].includes(variant)
      "
    >
      <Typography
        v-if="style.headline"
        class="transition duration-500"
        :class="style.headline"
      >
        {{ isDirectAd ? creative?.headline : headline }}
      </Typography>

      <div v-if="style.publisher" :class="style.publisher.wrapper">
        <NuxtImg
          v-if="style.publisher.icon"
          :class="style.publisher.icon"
          :src="publisherIconUrl"
        />
        <div v-if="style.publisher" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ !isDirectAd ? publisherName : creative?.brand }}
          </Typography>
          <Typography v-if="!isDirectAd" :class="style.publisher.text">
            ·
          </Typography>
          <Typography v-if="!isDirectAd" :class="style.publisher.text">
            {{ useFormattedTimeDifference(props.publishedDate) }}
          </Typography>
        </div>
      </div>
    </template>

    <div
      v-if="
        (!isNativeAd && variant?.startsWith('yolo')) ||
        variant === 'transparent' ||
        variant === '2x2'
      "
      class="flex flex-col self-center transition duration-300"
    >
      <Typography
        v-if="props.headline"
        class="transition duration-500"
        :class="style.headline"
      >
        {{ headline }}
      </Typography>
      <div
        :class="{
          'flex flex-row gap-10 px-4 mb-4 items-center': variant === '2x2',
          'flex flex-col transition duration-300': variant !== '2x2',
          'order-4': design === 'msn',
        }"
      >
        <Typography
          v-if="style.subtitle"
          class="flex-1"
          :class="style.subtitle"
        >
          {{ subtitle }}
        </Typography>
        <Button
          v-if="style.button"
          :class="style.button"
          size="small"
          label-font-variant="text-xs"
          @click="handleClick"
        >
          {{ t('Read more') }}
        </Button>
      </div>
      <div v-if="style.publisher" :class="style.publisher.wrapper">
        <NuxtImg
          v-if="style.publisher.icon"
          class="transition duration-300"
          :class="style.publisher.icon"
          :src="publisherIconUrl"
        />
        <div v-if="!style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherName }}
          </Typography>
          <Typography :class="style.publisher.text">·</Typography>
          <Typography :class="style.publisher.text">
            {{ useFormattedTimeDifference(props.publishedDate) }}
          </Typography>
        </div>
        <div v-if="style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherName }}
          </Typography>
        </div>
      </div>
    </div>
    <div
      v-if="!isNativeAd && variant === '2x2-top-headline'"
      class="flex flex-col self-center transition duration-300"
    >
      <Typography
        v-if="props.headline"
        class="transition duration-500"
        :class="style.headline"
      >
        {{ headline }}
      </Typography>
      <NuxtImg
        v-if="!isNativeAd && variant === '2x2-top-headline' && style.image"
        class="transition duration-300"
        role="img"
        :aria-label="`Image for ${props.ad ? 'Partner Content' : headline}`"
        :class="style.image"
        :src="isDirectAd ? creative?.imageUrl : thumbnailUrl"
      />
      <div v-if="style.publisher" :class="style.publisher.wrapper">
        <NuxtImg
          v-if="style.publisher.icon"
          class="transition duration-300"
          :class="style.publisher.icon"
          :src="publisherIconUrl"
        />
        <div v-if="!style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherName }}
          </Typography>
          <Typography :class="style.publisher.text">·</Typography>
          <Typography :class="style.publisher.text">
            {{ useFormattedTimeDifference(props.publishedDate) }}
          </Typography>
        </div>
        <div v-if="style.publisher.ad" class="flex flex-row gap-1">
          <Typography :class="style.publisher.text">
            {{ publisherName }}
          </Typography>
        </div>
      </div>
      <div
        :class="{
          'flex flex-row gap-10 px-4 mb-4 items-center':
            variant === '2x2-top-headline',
        }"
      >
        <Typography
          v-if="style.subtitle"
          class="flex-1"
          :class="style.subtitle"
        >
          {{ subtitle }}
        </Typography>
        <Button
          v-if="style.button"
          :class="style.button"
          size="small"
          label-font-variant="text-xs"
          @click="handleClick"
        >
          {{ t('Read more') }}
        </Button>
      </div>
    </div>
  </a>
</template>
